import { storeToRefs } from "pinia"
import { setup, createActor, fromPromise } from "xstate"
import type { MoveBisMachineEventType } from "./types"
import router from "@/router"
import { useAnswersStore } from "@/features/movebis/store/answers.store"
import { useQuestionsStore } from "@/features/movebis/store/questions.store"
import { useUserStore } from "@/stores/user"
import { globalActor } from "@/machines/globalMachine"
import { useI18nStore } from "@/stores/i18n"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { nextTick } from "vue"
import type { GlobalMachineEvent } from "@/machines/types"
import { COOKIES } from "@/config/constants"

// Loader for MoveBis datas
// to redirect to Intro or WelcomeBack
const loadMoveBisData = fromPromise(async () => {
  const i18nStore = useI18nStore()
  const { fetchMoveBis, retakeTest } = useAnswersStore()
  const moveBis = await fetchMoveBis()

  const { fetchQuestions, fetchAudios } = useQuestionsStore()
  const { questionsLength } = storeToRefs(useQuestionsStore())
  const { assessmentPE } = storeToRefs(useUserStore())
  const { updateLocale, isCurrentLangFr } = i18nStore

  const currentMoveBis = assessmentPE.value?.assessments?.movebis

  const assessmentSummary = useUserStore().assessmentPE.assessments.movebis
  const isFinisedAndNotPassed =
    currentMoveBis?.status === "finish" && assessmentSummary?.not_passed

  // Legacy, the test has been not_passed and considered with status finish in assessment summary (old) but not in workflow and next_assessment (new)
  let assessmentStatus = isFinisedAndNotPassed ? "todo" : currentMoveBis?.status

  // status can be "todo", "inprogress" or "finish"
  switch (assessmentStatus) {
    case "todo":
      if (isCurrentLangFr()) {
        await updateLocale(1) // Force FR
      } else {
        await updateLocale(3) // Force EN for other langs
      }

      await fetchQuestions()
      await fetchAudios()
      return { status: "new" }

    case "inprogress":
      await fetchQuestions()
      await fetchAudios()
      // If there is less answers than questions, we need to show the questions
      if (moveBis.data.items.length < questionsLength.value) {
        return { status: "showQuestions" }
      }
      // If there is no native language, we need to show the native language screen
      else if (
        moveBis.data.extra?.native_language == undefined ||
        moveBis.data.extra?.native_language == null
      ) {
        return { status: "showNativeLanguage" }
      }
      // If there is no wholesome conditions, we need to show the wholesome conditions screen
      else if (
        moveBis.data.extra?.wholesome_conditions == undefined ||
        moveBis.data.extra?.wholesome_conditions == null
      ) {
        return { status: "showWholesomeConditions" }
      }
      // If the user has complete every steps but status is inprogress, we need to send the results to backend
      else {
        return { status: "showLoadResults" }
      }

    case "finish":
      if (currentMoveBis?.retake_test) {
        await retakeTest()
        await fetchQuestions()
        await fetchAudios()
        return { status: "new" }
      }
      return { status: "showFinal" }

    default:
      await fetchQuestions()
      await fetchAudios()
      return { status: "new" }
  }
})

export const moveBisMachine = setup({
  actors: {
    loadMoveBisData,
  },
  types: {} as {
    events: MoveBisMachineEventType
  },
  actions: {},
}).createMachine({
  id: "moveBisMachine",
  initial: "loading",
  states: {
    loading: {
      // entry: [() => router.replace({ name: "moveBisLoading" })],
      entry: [
        () => {
          if (router.currentRoute.value.name !== "moveBisLoading") {
            router.replace({ name: "moveBisLoading" })
          }
        },
      ],
      invoke: {
        id: "loadMoveBis",
        src: "loadMoveBisData",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "new",
            target: "intro",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showQuestions",
            target: "welcomeBack",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showNativeLanguage",
            target: "nativeLanguage",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showWholesomeConditions",
            target: "wholesomeConditions",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showLoadResults",
            target: "loadResults",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showFinal",
            target: "final",
          },
        ],
      },
    },
    intro: {
      entry: [() => router.replace({ name: "moveBisIntro" })],
      on: {
        done: { target: "rules" },
      },
    },
    welcomeBack: {
      entry: [() => router.replace({ name: "moveBisWelcomeBack" })],
      on: {
        done: { target: "test" },
      },
    },
    rules: {
      entry: [() => router.replace({ name: "moveBisRules" })],
      on: {
        done: { target: "practice" },
        back: { target: "intro" },
      },
    },
    practice: {
      entry: [() => router.replace({ name: "moveBisPractice" })],
      on: {
        done: { target: "tuto" },
        back: { target: "rules" },
      },
    },
    tuto: {
      entry: [() => router.replace({ name: "moveBisTuto" })],
      on: {
        done: { target: "tutoEnd" },
      },
    },
    tutoEnd: {
      entry: [() => router.replace({ name: "moveBisTutoEnd" })],
      on: {
        done: { target: "test" },
        back: { target: "tuto" },
      },
    },
    test: {
      entry: [() => router.replace({ name: "moveBisTest" })],
      on: {
        done: { target: "nativeLanguage" },
      },
    },
    nativeLanguage: {
      entry: [() => router.replace({ name: "moveBisNativeLanguage" })],
      on: {
        done: { target: "wholesomeConditions" },
      },
    },
    wholesomeConditions: {
      entry: [() => router.replace({ name: "moveBisWholesomeConditions" })],
      on: {
        true: { target: "loadResults" },
        false: { target: "loadResults" },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "moveBisLoadResults" })],
      on: {
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export const moveBisActor = createActor(moveBisMachine)

moveBisActor.subscribe({
  async complete() {
    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())

    const pm_uuid = useCookies().get(COOKIES.PM_UUID)
    const company_uuid = useCookies().get(COOKIES.COMPANY_UUID)
    await fetchNextAssessment(pm_uuid ?? null, company_uuid ?? null)

    await nextTick()

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.PM_UUID)
      useCookies().remove(COOKIES.COMPANY_UUID)
      globalActor.send({ type: "GLOBAL.MOVEBIS.DONE" })
    }
  },
})
